import { useEffect, useMemo, useState } from "react";
import { Leaf, Loader2, Package, TrendingDown, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, IDR, NUM, formatErr } from "@/lib/api";

const COLORS = ["#059669", "#0891B2", "#0B4F78", "#F59E0B", "#8B5CF6"];

function Metric({ label, value, hint, tone = "slate", diff }) {
  const toneMap = { slate: "text-slate-900", emerald: "text-emerald-700", rose: "text-rose-600" };
  return (
    <div>
      <div className="gc-label">{label}</div>
      <div className={`font-cabinet text-2xl font-black mt-1 ${toneMap[tone]}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      {diff !== undefined && (
        <div className={`mt-1 text-xs font-semibold inline-flex items-center gap-1 ${diff > 0 ? "text-rose-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"}`}>
          {diff > 0 ? <TrendingUp className="w-3 h-3" /> : diff < 0 ? <TrendingDown className="w-3 h-3" /> : null}
          {diff > 0 ? "+" : ""}{NUM(diff, 2)}%
        </div>
      )}
    </div>
  );
}

export default function Comparison() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try {
      const { data: p } = await api.get("/products");
      setProducts(p);
      // preselect 2 with different types if available
      const lc = p.find((x) => x.product_type === "low_carbon");
      const conv = p.find((x) => x.product_type === "conventional");
      const initial = [lc?.id, conv?.id].filter(Boolean);
      if (initial.length >= 2) setSelected(initial);
      else if (p.length >= 2) setSelected([p[0].id, p[1].id]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  })(); }, []);

  useEffect(() => {
    if (selected.length < 2) { setData(null); return; }
    (async () => {
      try {
        const { data: c } = await api.get(`/comparison/products?product_ids=${selected.join(",")}`);
        setData(c);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, [selected]);

  const toggle = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-3));
  };

  const chartData = useMemo(() => {
    if (!data?.items?.length) return [];
    return data.items.map((it) => ({
      name: it.product.code,
      full: it.product.name,
      HPP_per_unit: Math.round(it.hpp?.hpp_per_unit || 0),
      "Emisi (kg CO₂)": Number((it.hpp?.emissions_per_unit_kg_co2 || 0).toFixed(4)),
      "Total Biaya": Math.round(it.hpp?.total_cost || 0),
    }));
  }, [data]);

  if (loading) return <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  const diffPercent = (a, b) => {
    const av = Number(a || 0); const bv = Number(b || 0);
    if (!bv) return null;
    return ((av - bv) / bv) * 100;
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="gc-label">Analisis</span>
        <h1 className="gc-h1 mt-1">Perbandingan Produk</h1>
        <p className="mt-2 text-slate-500 max-w-2xl">Bandingkan HPP, komposisi biaya, biaya lingkungan, dan estimasi emisi antar produk (contohnya paving rendah karbon vs konvensional). Data diambil dari perhitungan HPP terbaru per produk.</p>
      </div>

      <div className="gc-card">
        <div className="gc-label mb-3">Pilih 2–3 produk untuk dibandingkan</div>
        <div className="flex flex-wrap gap-2">
          {products.map((p) => {
            const on = selected.includes(p.id);
            return (
              <button key={p.id} onClick={() => toggle(p.id)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 border text-sm transition-all ${on ? "bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-500/20" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"}`}
                data-testid={`select-product-${p.code}`}>
                <Package className="w-4 h-4" />
                <span className="font-semibold">{p.code}</span> <span className="opacity-90">{p.name}</span>
                {p.product_type === "low_carbon" && <Leaf className="w-3.5 h-3.5" />}
              </button>
            );
          })}
        </div>
      </div>

      {selected.length < 2 && (
        <div className="gc-card text-sm text-slate-500">Pilih minimal 2 produk untuk memulai perbandingan.</div>
      )}

      {data?.items?.length >= 2 && (
        <>
          {/* Side-by-side cards */}
          <div className={`grid gap-5 ${data.items.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
            {data.items.map((it, idx) => {
              const other = data.items.filter((_, i) => i !== idx);
              const otherHpp = other[0]?.hpp?.hpp_per_unit;
              const otherEmis = other[0]?.hpp?.emissions_per_unit_kg_co2;
              const hppDiff = diffPercent(it.hpp?.hpp_per_unit, otherHpp);
              const emisDiff = diffPercent(it.hpp?.emissions_per_unit_kg_co2, otherEmis);
              const isLc = it.product.product_type === "low_carbon";

              return (
                <div key={it.product.id} className={`gc-card border-2 ${isLc ? "border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white" : "border-slate-200"}`} data-testid={`card-compare-${it.product.code}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="gc-label">{isLc ? "Rendah Karbon" : "Konvensional"}</div>
                      <h3 className="gc-h3 mt-1">{it.product.name}</h3>
                      <div className="text-xs text-slate-500 mt-0.5">{it.product.code} • {it.product.dimensions || "-"}</div>
                    </div>
                    {isLc && <Leaf className="w-6 h-6 text-emerald-500" />}
                  </div>

                  {!it.hpp ? (
                    <div className="mt-6 text-sm text-slate-500 rounded-xl bg-slate-50 border border-slate-200 p-4">
                      Belum ada perhitungan HPP untuk produk ini. Buka menu <strong>Perhitungan HPP</strong> untuk menghitungnya.
                    </div>
                  ) : (
                    <div className="mt-5 space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <Metric label="HPP / Unit" value={IDR(it.hpp.hpp_per_unit)} tone={hppDiff < 0 ? "emerald" : hppDiff > 0 ? "rose" : "slate"} diff={hppDiff} hint={`No: ${it.hpp.calc_no}`} />
                        <Metric label="Total Biaya" value={IDR(it.hpp.total_cost)} hint={`${NUM(it.hpp.good_qty || it.hpp.target_qty, 0)} unit`} />
                      </div>

                      <div className="rounded-2xl bg-white border border-slate-200 p-4">
                        <div className="gc-label !text-emerald-700 flex items-center gap-1.5"><Leaf className="w-3.5 h-3.5" /> Estimasi Emisi</div>
                        <div className="mt-2 grid grid-cols-2 gap-3">
                          <div><div className="text-xs text-slate-500">Per Unit</div><div className={`font-cabinet text-xl font-black ${emisDiff < 0 ? "text-emerald-700" : "text-slate-900"}`}>{NUM(it.hpp.emissions_per_unit_kg_co2, 4)} <span className="text-xs text-slate-500">kg CO₂eq</span></div></div>
                          <div><div className="text-xs text-slate-500">Total Batch</div><div className="font-cabinet text-xl font-black text-slate-900">{NUM(it.hpp.estimated_emissions_kg_co2, 3)}</div></div>
                        </div>
                        {emisDiff !== null && (
                          <div className={`mt-2 text-xs font-semibold ${emisDiff < 0 ? "text-emerald-700" : "text-rose-600"}`}>
                            {emisDiff < 0 ? "−" : "+"}{NUM(Math.abs(emisDiff), 1)}% dibanding produk lain
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="gc-label mb-2">Komposisi Biaya</div>
                        <div className="space-y-1.5 text-sm">
                          {[
                            { label: "Bahan Baku", value: it.hpp.cost_materials },
                            { label: "Tenaga Kerja", value: it.hpp.cost_labor },
                            { label: "Overhead Variabel", value: it.hpp.cost_overhead_variable },
                            { label: "Overhead Tetap", value: it.hpp.cost_overhead_fixed },
                            { label: "Lingkungan", value: it.hpp.cost_environmental },
                          ].filter((c) => c.value > 0).map((c) => {
                            const pct = (c.value / it.hpp.total_cost) * 100;
                            return (
                              <div key={c.label}>
                                <div className="flex justify-between text-xs"><span className="text-slate-600">{c.label}</span><span className="font-semibold text-slate-900">{IDR(c.value)} ({NUM(pct, 1)}%)</span></div>
                                <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} /></div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 text-center rounded-2xl bg-slate-900 text-white p-4">
                        <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Batch</div><div className="font-cabinet text-lg font-black">{NUM(it.totals.batches, 0)}</div></div>
                        <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Produk Baik</div><div className="font-cabinet text-lg font-black text-emerald-300">{NUM(it.totals.good_qty, 0)}</div></div>
                        <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Waste</div><div className="font-cabinet text-lg font-black text-amber-300">{NUM(it.totals.reject_qty, 0)}</div></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-5">
            <div className="gc-card">
              <span className="gc-label">Perbandingan HPP / Unit</span>
              <div className="h-64 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `Rp ${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => IDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="HPP_per_unit" name="HPP / Unit" radius={[8, 8, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="gc-card">
              <span className="gc-label">Estimasi Emisi / Unit (kg CO₂eq)</span>
              <div className="h-64 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="Emisi (kg CO₂)" radius={[8, 8, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={i === 0 ? "#059669" : "#F59E0B"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800 flex items-start gap-2">
            <span className="mt-0.5">⚠️</span>
            <div>Estimasi emisi dihitung berdasarkan faktor emisi yang dimasukkan pengguna pada setiap bahan (kg CO₂eq per satuan). Ini <strong>bukan hasil pengukuran laboratorium otomatis</strong>. Perbarui faktor emisi pada halaman <em>Bahan Baku</em> agar hasil semakin akurat.</div>
          </div>
        </>
      )}
    </div>
  );
}
